import { Configuration, Currency, CurrencyApi } from "hotdesk_typescript";
import { fetchRemoteConfig } from "../common-classes/firebase.service";
import { RemoteConfigKeys } from "../enum/remote-keys";
import { currencyForCountry } from "../helpers/country-currency";

const FALLBACK_CURRENCY: Currency = { code: 'USD', name: 'USD' };
/** How long to wait for Remote Config before using the IP country instead. */
const REMOTE_CONFIG_TIMEOUT_MS = 5000;

export class CurrencyService {
    currencyApi;
    currencyList!: Currency[];
    defaultCurrency!: Currency;

    constructor(
        apiConfig: Configuration,
    ) {
        this.currencyApi = new CurrencyApi(apiConfig);
    }
    /**
     * The visitor's default currency. Firebase Remote Config's
     * `default_currency` holds the per-country mapping and wins when it
     * answers. When it can't be reached (ad blockers and privacy browsers
     * block it) or names a currency we don't list, fall back to the IP
     * country's own currency, and only then to USD.
     */
    async getDefaultCurrency(
        currencies: Currency[],
        getIpCountry: () => Promise<string | null>
    ) {
        const byCode = (code: string | null | undefined) =>
            code ? currencies.find((c) => c.code === code) : undefined;

        try {
            // Remote Config can hang rather than fail (e.g. its IndexedDB
            // store blocked by another tab), so don't wait on it forever.
            const firebaseCurrency = await Promise.race([
                fetchRemoteConfig(RemoteConfigKeys.default_currency, 'string') as Promise<string>,
                new Promise<null>((resolve) =>
                    setTimeout(() => resolve(null), REMOTE_CONFIG_TIMEOUT_MS)
                )
            ]);
            const fromFirebase = byCode(firebaseCurrency);
            if (fromFirebase) return fromFirebase;
        } catch (error) {
            console.error('Error fetching default currency from remote config:', error);
        }

        try {
            const fromIp = byCode(currencyForCountry(await getIpCountry()));
            if (fromIp) return fromIp;
        } catch (error) {
            console.error('Error resolving currency from IP country:', error);
        }

        return byCode(FALLBACK_CURRENCY.code) ?? FALLBACK_CURRENCY;
    }

    async getCurrencies(browserLanguage: string = 'en') {
        try {
            const params = {
                limit: 200,
                offset: 0,
                lang: browserLanguage
            };

            const currencyListResponse = await this.currencyApi.currencyList(
                params
            );
            this.currencyList = currencyListResponse.results;
            return { currencyList: this.currencyList};
        } catch (error) {
            console.error('Error fetching currencies:', error);
            throw new Error(
                'Failed to fetch currencies. Please try again later.'
            );
        }
    }

    currencyConvert(
        currencyList: Currency[],
        userCurrencyCode: string,
        baseCurrencyCode: string,
        amount: number
    ): number {
        if (!currencyList || currencyList.length === 0) {
            return amount;
        }
        const baseCurrency = currencyList.find(
            (currency) => currency.code === baseCurrencyCode
        );
        const userCurrency = currencyList.find(
            (currency) => currency.code === userCurrencyCode
        );
        if (!!baseCurrency?.factor && !!userCurrency?.factor) {
            return parseFloat(
                (amount * (baseCurrency.factor / userCurrency.factor)).toFixed(
                    2
                )
            );
        } else {
            console.error('Error converting currency');
            return amount;
        }
    }
}